<script setup>
/**
 * Guided anchor setup (PD-1258) — the Vue 2 twin of beta's CcAnchorSetup, both
 * faithful ports of the prototype's `LvlAnchorSetup` (lvl/anchor-wizard.jsx),
 * markup and copy included.
 *
 * Every rule lives in the shared `anchorSetupWizard` composable, so this file is
 * presentation only and the two portals cannot drift on behaviour. Differences
 * from beta are cosmetic and forced by the stack: `v-icon`/`mdi-*` instead of the
 * Material Symbols ligature spans, and scss instead of plain css.
 *
 * Nothing is written until Confirm on the review step.
 */
import { computed } from 'vue';
import { anchorSuggestionBadge } from '@rm-ui/levelling/levellingMeta';

const props = defineProps({
  /** The anchorSetupWizard() self object — MUST be markRaw'd by the host, or Vue
      2's observer unwraps its refs and every `.value` write throws. */
  w: { type: Object, required: true },
  /** The client's current ceiling before any change, for the "moves from X" line. */
  ceiling: { type: Object, default: null },
});

const emit = defineEmits(['close', 'evaluate']);

const w = props.w;

/* Badge copy lives in shared meta so the two portals cannot drift on it — the tooltip
   has to distinguish evidence from a guess, which is the entire point of the badge. */
const ceoBadge = computed(() => (w.ceoSuggested.value
  ? anchorSuggestionBadge({ fromTitle: w.ceoSuggested.value.fromTitle, kind: 'ceo' })
  : null));
function famBadge(family) {
  return anchorSuggestionBadge({ fromTitle: !!family.suggWeak, kind: 'family' });
}
function headcountTitle(n) {
  return n === 1
    ? '1 incumbent (indicative) — one-off roles make poor anchors unless they are executives.'
    : `${n} incumbents (indicative) — populous roles make strong anchors.`;
}
/** Anchor / Evaluated / Needs evaluation — the state of a single pick. */
function stateOf(role, already) {
  if (already) return { cls: 'is-on', mdi: 'mdi-anchor', label: 'Anchor' };
  if (w.evaluated(role)) return { cls: 'is-ok', mdi: 'mdi-check-decagram', label: 'Evaluated' };
  return { cls: 'is-pend', mdi: 'mdi-clock-outline', label: 'Needs evaluation' };
}
function isAlreadyAnchor(role) {
  return !!(role && (role.isAnchor || (role.levelling && role.levelling.is_anchor)));
}
/**
 * The role behind a spine pick, or undefined while the row is being chosen.
 *
 * Keyed by ROW KEY, not level code: a greenfield family's rungs are job-title tiers
 * and carry no code at all.
 */
function spineRole(family, rowKey) {
  return w.roleById.value[(w.spineSel.value[family] || {})[rowKey]];
}
function scatterRole(family) {
  return w.roleById.value[w.scatterSel.value[family]];
}
</script>

<template>
  <!-- Closing mid-batch would abandon a partially-applied save, so while the
       bulk write is in flight neither the scrim nor the X dismisses the wizard. -->
  <div class="lvl-modal-scrim" @click.self="!w.saving.value && emit('close')">
    <div class="lvl-modal lvl-anchw" role="dialog" aria-modal="true" aria-label="Set up anchor roles">
      <div class="lvl-modal-head lvl-anchw-head">
        <div>
          <div class="lvl-anchw-eyebrow">
            <v-icon size="14">mdi-anchor</v-icon> Anchor setup
          </div>
          <h3>{{ w.headTitle.value }}</h3>
        </div>
        <button class="lvl-anchw-x" aria-label="Close" :disabled="w.saving.value" @click="emit('close')">
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>

      <!-- Step rail. Completed steps are revisitable; later ones aren't reachable
           until their turn, so the sequence can't be skipped. -->
      <div v-if="!w.isAsideStep.value" class="lvl-anchw-rail" role="list" aria-label="Setup steps">
        <button
          v-for="(s, i) in w.steps.value"
          :key="s.key"
          role="listitem"
          class="lvl-anchw-railstep"
          :class="{ 'is-now': s.key === w.step.value, 'is-done': i < w.stepIndex.value }"
          :disabled="i > w.stepIndex.value"
          :aria-current="s.key === w.step.value ? 'step' : null"
          @click="i < w.stepIndex.value && w.goto(s.key)"
        >
          <span class="n">
            <v-icon v-if="i < w.stepIndex.value" size="13">mdi-check</v-icon>
            <template v-else>{{ i + 1 }}</template>
          </span>
          <span class="l">{{ s.label }}</span>
        </button>
      </div>

      <div class="lvl-modal-body lvl-anchw-body">
        <div v-if="w.loading.value" class="lvl-anchw-empty">Loading the register…</div>
        <div v-else-if="w.error.value" class="lvl-anchw-empty">{{ w.error.value }}</div>

        <!-- ===== intro ===== -->
        <template v-else-if="w.step.value === 'intro'">
          <p class="lvl-anchw-p">
            Anchor roles are evaluated reference points the rest of your register calibrates against. A
            common mistake is anchoring only the top of every family — senior one-offs with nothing
            underneath are the <em>hardest</em> roles to evaluate consistently. This setup works
            spine-first instead:
          </p>
          <ol class="lvl-anchw-steps">
            <li><strong>CEO first.</strong> Their evaluation fixes the ceiling — nothing should classify at or above it.</li>
            <li><strong>The leadership layer.</strong> The next most senior roles anchor the executive band (ordinary anchors — the ceiling stays on the CEO).</li>
            <li><strong>One family, top-to-bottom.</strong> A full spine gives you vertical calibration — every level checked against the one above and below.</li>
            <!-- AC8: cross-family is stated Optional on a greenfield register. With a
                 ceiling and one full spine there is already enough to start levelling,
                 and implying otherwise makes a new customer feel blocked. -->
            <li>
              <strong>Then spread wide.</strong> Cross-family picks weighted to populous,
              business-critical roles; one-off roles make poor anchors.<template
                v-if="w.isGreenfield.value"
              > Optional — you can come back to it.</template>
            </li>
            <!-- The fifth item names the step the rail actually ends on, which differs by
                 scenario. Greenfield has no coverage step to describe. -->
            <li v-if="w.isGreenfield.value">
              <strong>Review and go.</strong> A ceiling and one full spine is enough to start
              levelling. The framework grid tracks coverage as evaluations land, so nothing here
              has to be complete first.
            </li>
            <li v-else>
              <strong>Check mapping coverage.</strong> A quick check that enough roles are selected
              to suggest a framework mapping from your existing grades. You can proceed either way.
            </li>
          </ol>

          <!-- "What counts as a role?" — only worth asking when the register holds more
               than one type. The OPTIONS come from the server and can only ever be types
               with the levelling questionnaire attached; the SELECTION is session-scoped
               by product decision, so reopening the wizard can scope differently. -->
          <div v-if="w.showDocTypeFilter.value" class="lvl-anchw-doctypes">
            <span class="h"><v-icon size="15">mdi-file-document-outline</v-icon> What counts as a role?</span>
            <span class="s">
              Your register holds more than one document type. Anchoring only looks at the types you
              select — the rest stay out of the journey entirely.
            </span>
            <div class="lvl-anchw-doctype-row">
              <label
                v-for="d in w.docTypes.value"
                :key="d.k"
                class="lvl-anchw-doctype"
                :class="{ 'is-on': !!w.docSel.value[d.k] }"
              >
                <input
                  type="checkbox"
                  :checked="!!w.docSel.value[d.k]"
                  @change="w.toggleDocType(d.k, ($event.target).checked)"
                />
                <span class="t">{{ d.k }}</span>
                <span class="n">{{ d.n.toLocaleString() }}</span>
              </label>
            </div>
            <span v-if="!w.docTypesOn.value.length" class="s is-warn">
              Select at least one type — there is nothing to anchor otherwise.
            </span>
          </div>

        </template>

        <!-- ===== CEO & leadership ===== -->
        <template v-else-if="w.step.value === 'ceo'">
          <p class="lvl-anchw-p">
            Select your most senior role. It becomes an anchor <em>and</em> the CEO ceiling —
            classification stops offering levels at or above it.
          </p>
          <div v-if="ceiling" class="lvl-anchw-note-info">
            <v-icon size="17">mdi-format-vertical-align-top</v-icon>
            <span>
              Current ceiling: <strong>{{ ceiling.title }}</strong> ({{ ceiling.levelCode }}). Choosing a
              different role moves it — recorded on both roles.
            </span>
          </div>
          <div class="lvl-anchw-search">
            <v-icon size="17">mdi-magnify</v-icon>
            <input
              v-model="w.ceoQuery.value"
              placeholder="Search roles by title or family…"
              aria-label="Search roles"
            />
          </div>
          <div class="lvl-anchw-list" role="radiogroup" aria-label="CEO role">
            <button
              v-for="r in w.ceoOptions.value"
              :key="w.idOf(r)"
              role="radio"
              :aria-checked="w.ceoId.value === w.idOf(r)"
              class="lvl-anchw-opt"
              :class="{ 'is-on': w.ceoId.value === w.idOf(r) }"
              @click="w.ceoId.value = w.idOf(r)"
            >
              <span class="lvl-anchw-opt-main">
                <span class="t">{{ w.titleOf(r) }}</span>
                <span class="s">
                  {{ [r.family, w.levelCodeOf(r) ? 'Evaluated · ' + w.levelCodeOf(r) : 'Not yet levelled'].filter(Boolean).join(' · ') }}
                </span>
              </span>
                <!-- AC: the top candidate is BADGED, not pre-selected. The ceiling
                     silently hides levels from every future classification, so it stays
                     a deliberate pick. The tooltip names the SIGNAL — evaluated
                     seniority is evidence, a job title is a guess to be checked. -->
                <span
                  v-if="ceoBadge && w.ceoSuggested.value && w.ceoSuggested.value.id === w.idOf(r)"
                  class="lvl-anchw-sugg-tag"
                  :class="{ 'is-weak': ceoBadge.weak }"
                  :title="ceoBadge.tip"
                >{{ ceoBadge.label }}</span>
              <span v-if="w.levelCodeOf(r)" class="lvl-anchw-code">{{ w.levelCodeOf(r) }}</span>
              <span v-else class="lvl-anchw-needs">Needs evaluation</span>
              <v-icon v-if="w.ceoId.value === w.idOf(r)" size="17">mdi-check-circle</v-icon>
            </button>
            <!-- Reachable: the register is returned whether or not anything is
                 levelled, so a fresh client lands here and is told what to do. -->
            <div v-if="!w.ceoOptions.value.length" class="lvl-anchw-empty">
              <template v-if="w.ceoQuery.value.trim()">No roles match “{{ w.ceoQuery.value.trim() }}”.</template>
              <template v-else>
                Nothing is levelled yet, so there is no seniority to rank by. Search for your most senior
                role — “Chief Executive”, for example — and pick it here.
              </template>
            </div>
          </div>

          <template v-if="w.leadershipOptions.value.length">
            <div class="lvl-anchw-subhead">
              Leadership layer
              <span class="s">the next most senior roles — they anchor the executive band</span>
            </div>
            <div class="lvl-anchw-list">
              <label
                v-for="r in w.leadershipOptions.value"
                :key="'l' + w.idOf(r)"
                class="lvl-anchw-opt lvl-anchw-opt--check"
                :class="{ 'is-on': w.leadershipSel.value[w.idOf(r)] }"
              >
                <input
                  type="checkbox"
                  :checked="!!w.leadershipSel.value[w.idOf(r)]"
                  @change="w.toggleLeadership(w.idOf(r), $event.target.checked)"
                />
                <span class="lvl-anchw-opt-main">
                  <span class="t">{{ w.titleOf(r) }}</span>
                  <span class="s">{{ r.family }}</span>
                </span>
                <span
                  class="lvl-anchw-hc"
                  :class="{ 'is-one': w.headcount(r) === 1 }"
                  :title="headcountTitle(w.headcount(r))"
                ><v-icon size="12">mdi-account-group</v-icon> {{ w.headcount(r) }}</span>
                <span class="lvl-anchw-code">{{ w.levelCodeOf(r) }}</span>
              </label>
            </div>
          </template>
        </template>

        <!-- ===== family spine ===== -->
        <template v-else-if="w.step.value === 'spine'">
          <p class="lvl-anchw-p">
            <strong>What to do:</strong> tick one family (two at most), then confirm one role at each of
            its levels, top to bottom. A full ladder in one place means every level has the one above and
            below it to compare against.
          </p>
          <p class="lvl-anchw-p lvl-anchw-p--dim">
            Pick a family that runs deep <em>and</em> employs a lot of people — one most of the business
            would recognise, like operations or finance. Levels nobody sits at are skipped, not gaps.
          </p>
          <div class="lvl-anchw-fams">
            <div
              v-for="g in w.spineCandidates.value"
              :key="g.family"
              class="lvl-anchw-fam"
              :class="{ 'is-off': !w.spineFamilies.value.includes(g.family) }"
            >
              <label
                class="lvl-anchw-fam-check"
                :class="{ 'is-disabled': !w.spineFamilies.value.includes(g.family) && w.spineFamilies.value.length >= 2 }"
              >
                <input
                  type="checkbox"
                  :checked="w.spineFamilies.value.includes(g.family)"
                  :disabled="!w.spineFamilies.value.includes(g.family) && w.spineFamilies.value.length >= 2"
                  @change="w.toggleSpineFamily(g.family)"
                />
                <span class="fam">{{ g.family }}</span>
                <!-- The badge must NAME THE SIGNAL it actually used. With levels present
                     that is depth then people; with none, there is no ladder to read and
                     it falls back to the size of the family — claiming "deepest ladder"
                     there would be simply false. -->
                <span
                  v-if="w.spineCandidates.value[0] && w.spineCandidates.value[0].family === g.family"
                  class="lvl-anchw-sugg-tag"
                  :class="{ 'is-weak': !g.depth }"
                  :title="g.depth
                    ? 'Suggested because it has the most levels filled and the most people in them — the two things that make a spine easy to calibrate.'
                    : 'Nothing on this register is levelled yet, so there is no ladder depth to read. Suggested because it holds the most roles and the most people — the family most of the business will recognise.'"
                >{{ g.depth ? 'Suggested — deepest ladder, most people' : 'Suggested — most roles, most people' }}</span>
                <!-- Roles and people ALWAYS, with the ladder appended where there is one.
                     These two counts are what the row is for; making them an either/or with
                     depth meant a levelled register never showed them at all. -->
                <span class="n" title="Role and people counts come from the register. Headcount is indicative.">
                  {{ g.list.length }} role{{ g.list.length === 1 ? '' : 's' }} ·
                  {{ g.people }} {{ g.people === 1 ? 'person' : 'people' }}<template
                    v-if="g.depth"
                  > · {{ g.depth }} populated level{{ g.depth === 1 ? '' : 's' }} ·
                    {{ g.levelledN }} levelled</template><template v-else> · nothing levelled yet</template>
                </span>
              </label>

              <div v-if="w.spineFamilies.value.includes(g.family)" class="lvl-anchw-spine">
                <!-- AC1: say it in the open, not only in a tooltip on each rung. Without
                     this the tier headings look like levels the framework does not have. -->
                <div v-if="!g.codes.length" class="lvl-note lvl-note--info lvl-anchw-spinenote">
                  <v-icon size="17">mdi-information-outline</v-icon>
                  <span>
                    Nothing in this family is levelled yet, so there are no levels to lay the ladder
                    against. The roles are grouped by job title instead — tick the ones that form the
                    ladder top to bottom, and evaluation will set their real levels.
                  </span>
                </div>
                <!-- Column header. The rows are a grid on the same template, so the
                     labels sit over the values they name. -->
                <div class="lvl-anchw-spinehead" aria-hidden="true">
                  <span></span><span>Level</span><span>Anchor role</span><span>People</span><span>Status</span>
                </div>
                <div
                  v-for="row in w.spineRowsFor(g)"
                  :key="row.key"
                  class="lvl-anchw-spinerow"
                  :class="{ 'is-off': !(w.spineSel.value[g.family] || {})[row.key] }"
                >
                  <input
                    type="checkbox"
                    :checked="!!(w.spineSel.value[g.family] || {})[row.key]"
                    :aria-label="'Anchor at ' + row.label"
                    @change="w.toggleSpineLevel(g.family, row.key, $event.target.checked)"
                  />
                                    <!-- A rung is a LEVEL where levels exist and a job-title TIER where
                       none do. The tier is a display grouping, never a grade, so it must
                       not be dressed as a level chip. -->
                  <span v-if="!row.byTitle" class="lvl-anchw-code">{{ row.label }}</span>
                  <span
                    v-else
                    class="lvl-anchw-tier"
                    title="Grouped by job title — nothing in this family is levelled yet, so there are no levels to build the ladder from."
                  >{{ row.label }}</span>
                  <template v-if="(w.spineSel.value[g.family] || {})[row.key]">
                    <span class="lvl-selwrap lvl-anchw-selwrap">
                      <select
                        :value="(w.spineSel.value[g.family] || {})[row.key]"
                        :aria-label="'Anchor for ' + g.family + ' at ' + row.label"
                        @change="w.setSpineLevel(g.family, row.key, Number($event.target.value))"
                      >
                        <!-- assemblePicks dedupes SILENTLY, so a role taken by another
                             step looked accepted here and then vanished from the review
                             count. ownTag is what stops this row disabling its own
                             current value. -->
                        <option
                          v-for="r in row.at"
                          :key="w.idOf(r)"
                          :value="w.idOf(r)"
                          :disabled="!!w.claimOf(w.idOf(r), 'spine:' + g.family + ':' + row.key)"
                        >
                          {{ w.titleOf(r) }} · {{ w.headcount(r) }} {{ w.headcount(r) === 1 ? 'person' : 'people' }}<template
                            v-if="w.claimOf(w.idOf(r), 'spine:' + g.family + ':' + row.key)"
                          > — already selected ({{ w.claimOf(w.idOf(r), 'spine:' + g.family + ':' + row.key) }})</template>
                        </option>
                      </select>
                      <v-icon size="18">mdi-chevron-down</v-icon>
                    </span>
                    <span
                      class="lvl-anchw-hc"
                      :class="{ 'is-one': w.headcount(spineRole(g.family, row.key)) === 1 }"
                      :title="headcountTitle(w.headcount(spineRole(g.family, row.key)))"
                    ><v-icon size="12">mdi-account-group</v-icon>
                      {{ w.headcount(spineRole(g.family, row.key)) }}
                    </span>
                    <span
                      class="lvl-anchw-state"
                      :class="stateOf(spineRole(g.family, row.key), isAlreadyAnchor(spineRole(g.family, row.key))).cls"
                    >
                      <v-icon size="13">{{ stateOf(spineRole(g.family, row.key), isAlreadyAnchor(spineRole(g.family, row.key))).mdi }}</v-icon>
                      {{ stateOf(spineRole(g.family, row.key), isAlreadyAnchor(spineRole(g.family, row.key))).label }}
                    </span>
                  </template>
                  <span v-else class="lvl-anchw-skip">Skipped — this level stays unanchored in the spine.</span>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- ===== cross-family scatter ===== -->
        <template v-else-if="w.step.value === 'scatter'">
          <p class="lvl-anchw-p">
            One anchor per remaining family. Suggestions weight <strong>populous roles near the top</strong>
            — multiple incumbents make a defensible reference; one-off roles don’t (executives excepted).
            Families with nothing levelled get no suggestion. Unevaluated picks become
            <strong>pending</strong> anchors with an evaluation task.
          </p>
          <!-- AC8: on a greenfield register this whole step is optional, and saying so
               plainly is the point — a new customer with a ceiling and one spine is ready
               to level, and an unstated expectation of full breadth reads as a blocker. -->
          <div v-if="w.isGreenfield.value" class="lvl-anchw-note-info">
            <v-icon size="17">mdi-information-outline</v-icon>
            <span>
              <strong>Optional.</strong> With the CEO ceiling and one full spine you have enough to
              start levelling. Pick what is obvious now and come back to the rest whenever you like —
              this step reopens from the control centre.
            </span>
          </div>
          <div class="lvl-anchw-fambar">
            <span class="lvl-anchw-search lvl-anchw-search--sm">
              <v-icon size="16">mdi-magnify</v-icon>
              <input v-model="w.familyQuery.value" placeholder="Search families…" aria-label="Search job families" />
            </span>
            <button
              class="lvl-anchw-chip"
              :class="{ 'is-on': w.gapsOnly.value }"
              :aria-pressed="w.gapsOnly.value"
              @click="w.gapsOnly.value = !w.gapsOnly.value"
            ><v-icon size="14">mdi-filter-variant</v-icon> Gaps only</button>
            <span class="lvl-anchw-fambar-r">
              <button class="lvl-anchw-bulk" @click="w.useAllSuggestions()">Use all suggestions</button>
              <button class="lvl-anchw-bulk" @click="w.clearShown()">Clear</button>
            </span>
          </div>

          <div v-for="grp in w.groupedFamilies.value" :key="grp.group" class="lvl-anchw-grp">
            <div class="lvl-anchw-grp-h">
              {{ grp.group }}
              <span class="n">{{ grp.items.length }} famil{{ grp.items.length === 1 ? 'y' : 'ies' }}</span>
            </div>
            <div class="lvl-anchw-fams">
              <div
                v-for="g in grp.items"
                :key="g.family"
                class="lvl-anchw-fam"
                :class="{ 'is-off': w.scatterSel.value[g.family] == null }"
              >
                <label class="lvl-anchw-fam-check">
                  <input
                    type="checkbox"
                    :checked="w.scatterSel.value[g.family] != null"
                    @change="w.setScatter(g.family, $event.target.checked
                      ? (g.suggested ? w.idOf(g.suggested) : '') : null)"
                  />
                  <span class="fam">{{ g.family }}</span>
                  <!-- Roles, people and levelled — all three, always, as the prototype has
                       it. "0 levelled" is informative on a new register, not noise. -->
                  <span class="n" title="Role and people counts come from the register. Headcount is indicative.">
                    {{ g.list.length }} role{{ g.list.length === 1 ? '' : 's' }} ·
                    {{ g.people }} {{ g.people === 1 ? 'person' : 'people' }} ·
                    {{ g.levelledN }} levelled
                  </span>
                  <!-- A title-derived suggestion is offered but BADGED as indicative. It
                       used to be withheld entirely ("No suggestion"), which left a
                       greenfield family with nothing to accept. -->
                  <span
                    v-if="!g.existing && g.suggWeak"
                    class="lvl-anchw-nosugg"
                    :title="famBadge(g).tip"
                  >{{ famBadge(g).label }}</span>
                  <span
                    v-else-if="!g.existing && !g.suggested"
                    class="lvl-anchw-nosugg"
                    title="Nothing in this family gives a usable signal — pick manually or skip."
                  >No suggestion</span>
                </label>

                <span v-if="w.scatterSel.value[g.family] != null" class="lvl-anchw-fam-pick">
                  <span class="lvl-selwrap lvl-anchw-selwrap">
                    <select
                      :value="w.scatterSel.value[g.family] || ''"
                      :aria-label="'Anchor for ' + g.family"
                      @change="w.setScatter(g.family, $event.target.value ? Number($event.target.value) : null)"
                    >
                      <option v-if="!g.suggested" value="">Choose a role…</option>
                      <!-- Same claim guard as the spine rows: a role the CEO, leadership
                           layer or a spine already holds must not be offered again. -->
                      <option
                        v-for="r in g.list"
                        :key="w.idOf(r)"
                        :value="w.idOf(r)"
                        :disabled="!!w.claimOf(w.idOf(r), 'fam:' + g.family)"
                      >
                        {{ w.titleOf(r) }}{{ w.levelCodeOf(r) ? ' · ' + w.levelCodeOf(r) : ' · not levelled' }} ·
                        {{ w.headcount(r) }} {{ w.headcount(r) === 1 ? 'person' : 'people' }}<template
                          v-if="w.claimOf(w.idOf(r), 'fam:' + g.family)"
                        > — already selected ({{ w.claimOf(w.idOf(r), 'fam:' + g.family) }})</template>
                      </option>
                    </select>
                    <v-icon size="18">mdi-chevron-down</v-icon>
                  </span>
                  <template v-if="scatterRole(g.family)">
                    <span
                      class="lvl-anchw-hc"
                      :class="{ 'is-one': w.headcount(scatterRole(g.family)) === 1 }"
                      :title="headcountTitle(w.headcount(scatterRole(g.family)))"
                    ><v-icon size="12">mdi-account-group</v-icon> {{ w.headcount(scatterRole(g.family)) }}</span>
                    <span
                      class="lvl-anchw-state"
                      :class="stateOf(scatterRole(g.family), isAlreadyAnchor(scatterRole(g.family))).cls"
                    >
                      <v-icon size="13">{{ stateOf(scatterRole(g.family), isAlreadyAnchor(scatterRole(g.family))).mdi }}</v-icon>
                      {{ stateOf(scatterRole(g.family), isAlreadyAnchor(scatterRole(g.family))).label }}
                    </span>
                  </template>
                  <span v-else class="lvl-anchw-state is-pend">Pick a role</span>
                </span>
                <span v-else class="lvl-anchw-skip">Skipped — this family stays without a calibration reference.</span>
              </div>
            </div>
          </div>
          <div v-if="!w.groupedFamilies.value.length" class="lvl-anchw-empty">
            No families match “{{ w.familyQuery.value }}”<template v-if="w.gapsOnly.value"> with anchors missing</template>.
          </div>
        </template>

        <!-- ===== coverage ===== -->
        <template v-else-if="w.step.value === 'coverage'">
          <!-- AC6: this step exists to answer "is there enough evidence to suggest a
               framework mapping from the existing grades" — not to grade coverage. It used
               to open with the floor definition, which is the same words the greenfield
               story explicitly rejected. Prototype copy, verbatim. -->
          <p class="lvl-anchw-p">
            A quick check that enough roles are selected to suggest a framework mapping from your
            existing grades. A grade needs <strong>{{ w.coverage.value.floor }} evaluated roles</strong>
            behind it before its mapping can be trusted, so this counts how many levels you are on track
            to reach. You can edit the selection later, and you can proceed even if coverage is not full
            yet.
          </p>
          <div v-if="w.coverage.value.pop" class="lvl-anchw-covstats">
            <span class="lvl-anchw-covstat">
              <b>{{ w.coverage.value.metNow }}</b> of {{ w.coverage.value.pop }} levels reach the floor
            </span>
            <!-- Only when the picks actually move it — an identical projected number is
                 noise dressed up as progress. -->
            <span
              v-if="w.coverage.value.metAfter > w.coverage.value.metNow"
              class="lvl-anchw-covstat is-proj"
            ><b>{{ w.coverage.value.metAfter }}</b> of {{ w.coverage.value.pop }} once these picks are evaluated</span>
            <span class="lvl-anchw-covstat is-gap"><b>{{ w.coverage.value.gaps.length }}</b> short</span>
          </div>

          <!-- Legend: "some but not enough" and "none at all" read very differently,
               and the chips alone don't say which is which. -->
          <div v-if="w.coverage.value.gaps.length" class="lvl-anchw-gapshead">
            Levels below the floor
            <span class="s">
              <span class="lvl-fw-cov is-thin">1/{{ w.coverage.value.floor }}</span> some anchors, not enough
              &nbsp;·&nbsp;
              <span class="lvl-fw-cov is-gap">0/{{ w.coverage.value.floor }}</span> none yet
            </span>
          </div>
          <div v-if="w.coverage.value.gaps.length" class="lvl-anchw-gaps">
            <span
              v-for="c in w.coverage.value.gaps.slice(0, 14)"
              :key="c.code"
              class="lvl-anchw-gapchip"
              :title="(c.meta.trackLabel || '') + ' · ' + c.n + ' role' + (c.n === 1 ? '' : 's')
                + ' at this level · ' + c.anch + ' evaluated anchor' + (c.anch === 1 ? '' : 's')
                + (c.proj ? ' + ' + c.proj + ' picked' : '')"
            >
              {{ c.code }}
              <span class="lvl-fw-cov" :class="c.anch + c.proj ? 'is-thin' : 'is-gap'">
                {{ c.anch + c.proj }}/{{ w.coverage.value.floor }}
              </span>
            </span>
            <span v-if="w.coverage.value.gaps.length > 14" class="lvl-anchw-gapmore">
              +{{ w.coverage.value.gaps.length - 14 }} more
            </span>
          </div>
          <div v-else class="lvl-anchw-note-info">
            <template v-if="w.coverage.value.pop">
              <v-icon size="17">mdi-check-decagram</v-icon>
              <span>Every populated level reaches the floor once these picks are evaluated.</span>
            </template>
            <template v-else>
              <v-icon size="17">mdi-timer-sand-empty</v-icon>
              <span>
                There is nothing to score yet: coverage counts evaluated anchors at levels people actually
                sit at, and no roles here carry a level. Your picks will start filling this in as their
                evaluations are completed — so a clean sheet here is expected on a new register, not a
                problem.
              </span>
            </template>
          </div>

          <div v-if="w.coverage.value.gaps.length" class="lvl-anchw-note-info">
            <v-icon size="17">mdi-information-outline</v-icon>
            <span>
              Thin levels usually fill naturally as more roles are evaluated — go back to add picks now,
              or continue and return later.
            </span>
          </div>
          <!-- Reachable for the same reason: unlevelled roles are in the payload,
               so they can be counted and reported rather than silently dropped. -->
          <p v-if="w.coverage.value.unlevelled" class="lvl-anchw-p lvl-anchw-p--dim">
            {{ w.coverage.value.unlevelled }} role{{ w.coverage.value.unlevelled === 1 ? ' is' : 's are' }}
            not yet levelled and can’t be counted towards coverage.
          </p>
        </template>

        <!-- ===== review ===== -->
        <template v-else-if="w.step.value === 'review'">
          <p class="lvl-anchw-p">
            Nothing is saved yet. Each change is recorded in the role’s history when you confirm.
          </p>
          <div class="lvl-anchw-rev">
            <div class="lvl-anchw-rev-row">
              <span class="lvl-anchw-rev-ic"><v-icon size="17">mdi-format-vertical-align-top</v-icon></span>
              <div class="lvl-anchw-rev-main">
                <span class="h">CEO ceiling</span>
                <span class="b">
                  <template v-if="w.ceoRole.value">
                    {{ w.titleOf(w.ceoRole.value) }}{{ w.levelCodeOf(w.ceoRole.value) ? ' · ' + w.levelCodeOf(w.ceoRole.value) : ' · needs evaluation' }}<template
                      v-if="w.ceilingMoves.value"> ({{ ceiling ? 'moves from ' + ceiling.title : 'new' }})</template><template
                      v-else> (unchanged)</template>
                  </template>
                  <template v-else>Not set — classification will keep offering every level.</template>
                </span>
              </div>
            </div>

            <!-- ONE row, not two: "N new anchors" plus a separate "N pending" read as
                 two unrelated facts. What the user needs before confirming is how much
                 of this is usable today, so the split is in the headline. -->
            <div class="lvl-anchw-rev-row">
              <span
                class="lvl-anchw-rev-ic"
                :class="{ 'is-warn': w.reviewSummary.value.allPending }"
              ><v-icon size="17">mdi-anchor</v-icon></span>
              <div class="lvl-anchw-rev-main">
                <span class="h">{{ w.reviewSummary.value.head }}</span>
                <span class="b">{{ w.reviewSummary.value.body }}</span>
              </div>
            </div>

            <div v-if="w.coverage.value.gaps.length" class="lvl-anchw-rev-row">
              <span class="lvl-anchw-rev-ic is-warn"><v-icon size="17">mdi-clipboard-check-outline</v-icon></span>
              <div class="lvl-anchw-rev-main">
                <span class="h">
                  {{ w.coverage.value.gaps.length }} level{{ w.coverage.value.gaps.length === 1 ? '' : 's' }} below the floor
                </span>
                <span class="b">
                  Acknowledged — classification in those areas will warn until more anchors are evaluated.
                </span>
              </div>
            </div>

            <div v-if="w.skippedFamilies.value.length" class="lvl-anchw-rev-row">
              <span class="lvl-anchw-rev-ic is-warn"><v-icon size="17">mdi-alert-octagon-outline</v-icon></span>
              <div class="lvl-anchw-rev-main">
                <span class="h">
                  {{ w.skippedFamilies.value.length }} famil{{ w.skippedFamilies.value.length === 1 ? 'y' : 'ies' }} without an anchor
                </span>
                <span class="b">
                  {{ w.skippedFamilies.value.slice(0, 6).map((f) => f.family).join(', ')
                  }}<template v-if="w.skippedFamilies.value.length > 6"> and {{ w.skippedFamilies.value.length - 6 }} more</template>.
                </span>
              </div>
            </div>
            <!-- Greenfield only: the coverage step is dropped from the rail because on a
                 new register it can only ever report failure. Dropping it silently would
                 imply coverage does not matter — it does, it just accrues later. This row
                 is the whole replacement. -->
            <div v-if="w.greenfieldCoverageNote.value" class="lvl-anchw-rev-row">
              <span class="lvl-anchw-rev-ic"><v-icon size="17">mdi-view-grid-outline</v-icon></span>
              <div class="lvl-anchw-rev-main">
                <span class="h">{{ w.greenfieldCoverageNote.value.head }}</span>
                <span class="b"><template
                  v-for="(seg, si) in w.greenfieldCoverageNote.value.body"
                ><strong v-if="seg.strong" :key="'s' + si">{{ seg.t }}</strong><template
                  v-else
                >{{ seg.t }}</template></template></span>
              </div>
            </div>
          </div>

          <!-- No setup-wide rationale box (Sasha, Aug): one sentence covering forty
               anchors says nothing defensible about any single one, and having it here
               AND on the role was duplication. The per-role rationale is the one that
               survives into audit — recommended, never required. -->
          <div class="lvl-note lvl-note--info lvl-anchw-revnote">
            <v-icon size="17">mdi-history</v-icon>
            <span>
              Every anchor is recorded with who set it and when. A written rationale is
              <strong>recommended but not required</strong>, and belongs on the role — add or refine it
              from any anchor’s record whenever you like.
            </span>
          </div>
        </template>

        <!-- ===== outstanding evaluations =====
             The state the wizard REOPENS into once anchors exist. A pending anchor is
             inert — nothing can be compared against it until it carries a full
             evaluation — so this is the work that actually remains, and it is
             register-wide rather than just this session's picks. -->
        <template v-else-if="w.step.value === 'tasks'">
          <p class="lvl-anchw-p">
            <template v-if="w.pendingAll.value.length === 1">
              1 role is marked as an anchor but still needs a full evaluation. Until it is evaluated it
              cannot be used as a reference — nothing else is compared against it.
            </template>
            <template v-else-if="w.pendingAll.value.length">
              {{ w.pendingAll.value.length }} roles are marked as anchors but still need a full
              evaluation. Until each one is evaluated it cannot be used as a reference — nothing else is
              compared against it.
            </template>
            <template v-else>Every anchor has been evaluated. There is nothing outstanding.</template>
          </p>
          <div v-if="w.pendingAll.value.length" class="lvl-anchw-tasks">
            <div class="lvl-anchw-tasks-h">Outstanding · {{ w.pendingAll.value.length }}</div>
            <div v-for="r in w.pendingAll.value" :key="'p' + w.idOf(r)" class="lvl-anchw-task">
              <v-icon size="15">mdi-clipboard-text-search-outline</v-icon> Evaluate {{ w.titleOf(r) }}
              <span class="fam">{{ r.family }}</span>
              <button
                class="lvl-anchw-bulk"
                :title="'Start a full evaluation of ' + w.titleOf(r) + '. It becomes a working anchor the moment the evaluation is saved.'"
                @click="emit('evaluate', r)"
              >Start</button>
            </div>
            <div class="lvl-anchw-tasks-f">
              Run them here, or invite the right colleague from the control centre. Each becomes a
              working reference automatically once its evaluation is saved.
            </div>
          </div>
        </template>

        <!-- ===== done ===== -->
        <template v-else-if="w.step.value === 'done'">
          <div class="lvl-anchw-done">
            <span class="lvl-anchw-done-ic"><v-icon size="22">mdi-anchor</v-icon></span>
            <p class="lvl-anchw-p">
              <strong>{{ (w.done.value || {}).newCount || 0 }} anchor{{ ((w.done.value || {}).newCount || 0) === 1 ? '' : 's' }} set</strong><template
                v-if="(w.done.value || {}).ceoRole">
                — {{ w.titleOf(w.done.value.ceoRole) }} is your CEO ceiling{{
                  w.levelCodeOf(w.done.value.ceoRole) ? ' (' + w.levelCodeOf(w.done.value.ceoRole) + ')' : ' once evaluated'
                }}</template>. Anchors now lead their band in calibration and appear as the reference
              inside the evaluation journey.
            </p>
          </div>

          <!-- Partial failure is reported, never swallowed. -->
          <div v-if="((w.done.value || {}).failed || []).length" class="lvl-anchw-note-info is-warn">
            <v-icon size="17">mdi-alert-circle-outline</v-icon>
            <span>
              {{ w.done.value.failed.length }} pick{{ w.done.value.failed.length === 1 ? '' : 's' }} could
              not be saved. The rest were applied — reopen setup to retry.
            </span>
          </div>

          <div v-if="((w.done.value || {}).pending || []).length" class="lvl-anchw-tasks">
            <div class="lvl-anchw-tasks-h">Evaluation tasks · {{ w.done.value.pending.length }}</div>
            <div v-for="r in w.done.value.pending" :key="'t' + w.idOf(r)" class="lvl-anchw-task">
              <v-icon size="15">mdi-clipboard-text-search-outline</v-icon> Evaluate {{ w.titleOf(r) }}
              <span class="fam">{{ r.family }}</span>
              <button class="lvl-anchw-bulk" @click="emit('evaluate', r)">Start</button>
            </div>
            <div class="lvl-anchw-tasks-f">
              Each pending anchor confirms automatically once a full evaluation is saved. Run them here, or
              invite the right colleague from the control centre. To come back to this list later, use
              <strong>Finish evaluations</strong> on the levelling control centre — the same button reopens
              these steps if you want to add more anchors.
            </div>
          </div>
        </template>
      </div>

      <div class="lvl-modal-foot lvl-anchw-foot">
        <span v-if="w.step.value === 'spine' || w.step.value === 'scatter'" class="lvl-anchw-count">
          {{ w.picks.value.length }} pick{{ w.picks.value.length === 1 ? '' : 's' }}<template
            v-if="w.newPicks.value.length"> · {{ w.newPicks.value.length }} new</template><template
            v-if="w.pendingPicks.value.length"> · {{ w.pendingPicks.value.length }} need evaluation</template>
          · nothing saved yet
        </span>

        <template v-if="w.step.value === 'intro'">
          <button class="lvl-btn lvl-btn--text" @click="emit('close')">Cancel</button>
          <button class="lvl-btn lvl-btn--primary" @click="w.next()">
            Let’s start <v-icon size="16">mdi-arrow-right</v-icon>
          </button>
        </template>
        <template v-else-if="w.step.value === 'ceo'">
          <button class="lvl-btn lvl-btn--text" @click="w.back()">Back</button>
          <button class="lvl-btn lvl-btn--primary" :disabled="!w.ceoId.value" @click="w.next()">
            Next: family spine <v-icon size="16">mdi-arrow-right</v-icon>
          </button>
        </template>
        <template v-else-if="w.step.value === 'spine'">
          <button class="lvl-btn lvl-btn--text" @click="w.back()">Back</button>
          <button class="lvl-btn lvl-btn--primary" @click="w.next()">
            Next: cross-family <v-icon size="16">mdi-arrow-right</v-icon>
          </button>
        </template>
        <template v-else-if="w.step.value === 'scatter'">
          <button class="lvl-btn lvl-btn--text" @click="w.back()">Back</button>
          <button class="lvl-btn lvl-btn--primary" :disabled="!w.picks.value.length" @click="w.next()">
            {{ w.scatterNextLabel.value }} <v-icon size="16">mdi-arrow-right</v-icon>
          </button>
        </template>
        <template v-else-if="w.step.value === 'coverage'">
          <button class="lvl-btn lvl-btn--text" @click="w.back()">Back</button>
          <button class="lvl-btn lvl-btn--primary" @click="w.next()">
            {{ w.coverage.value.gaps.length
              ? 'Review with ' + w.coverage.value.gaps.length + ' thin level' + (w.coverage.value.gaps.length === 1 ? '' : 's')
              : 'Review picks' }}
            <v-icon size="16">mdi-arrow-right</v-icon>
          </button>
        </template>
        <template v-else-if="w.step.value === 'review'">
          <button class="lvl-btn lvl-btn--text" :disabled="w.saving.value" @click="w.back()">Back</button>
          <button
            class="lvl-btn lvl-btn--primary"
            :disabled="!w.canConfirm.value || w.saving.value"
            @click="w.apply()"
          >
            <span v-if="w.saving.value" class="lvl-anchw-spin" aria-hidden="true"></span>
            <v-icon v-else size="16">mdi-anchor</v-icon>
            {{ w.saving.value
              ? 'Saving ' + w.savedCount.value + ' of ' + w.saveTotal.value + '…'
              : (w.newPicks.value.length
                ? 'Confirm ' + w.newPicks.value.length + ' anchor' + (w.newPicks.value.length === 1 ? '' : 's')
                : 'Confirm ceiling') }}
          </button>
        </template>
        <template v-else-if="w.step.value === 'tasks'">
          <!-- The numbered steps stay one click away, so this is a state to reopen
               into rather than a dead end. -->
          <button class="lvl-btn lvl-btn--text" @click="w.goto('intro')">
            <v-icon size="16">mdi-anchor</v-icon> Add more anchors
          </button>
          <button class="lvl-btn lvl-btn--primary" @click="emit('close')">Done</button>
        </template>
        <template v-else-if="w.step.value === 'done'">
          <button class="lvl-btn lvl-btn--primary" @click="emit('close')">Done</button>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* Ported from the prototype's .lvl-anchw-* rules (lvl/lvl.css) against the --cc-*
   tokens, which theme.scss declares GLOBALLY for light and dark — so the
   color-mix() washes recompute in dark mode for free. Deliberately not --v3-*:
   those are declared inside evaluationTheme.scss / v3Widgets.scss only, so in the
   control centre they resolve to their light-mode fallback and dark mode is
   silently ignored. Beta's twin uses the same --cc-* names and fallbacks. */
.lvl-modal-scrim {
  position: fixed;
  inset: 0;
  z-index: 300;
  background: rgba(16, 24, 40, 0.44);
  display: grid;
  place-items: center;
  padding: 24px;
}

.lvl-modal {
  display: flex;
  flex-direction: column;
  max-height: min(86vh, 760px);
  background: var(--cc-surface, #fff);
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 16px;
  box-shadow: 0 18px 48px rgba(16, 24, 40, 0.22);
  overflow: hidden;
}

/* The prototype sets 720px, but its rail predates the sixth step: six steps need
   ~815px, so at 720 they either overflow or wrap to a second row and stop reading
   as a sequence. Widened so the rail lays out on one line. */
.lvl-anchw { width: min(880px, 100%); }

.lvl-modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;

  h3 {
    margin: 0;
    font-size: 17px;
    font-weight: 700;
    color: var(--cc-text, #1f242d);
    line-height: 1.3;
  }
}
.lvl-anchw-head { padding-bottom: 10px; }

.lvl-anchw-x {
  flex: none;
  border: 0;
  background: transparent;
  cursor: pointer;
  color: var(--cc-text-3, #97a0af);
  display: inline-flex;
  padding: 2px;
  border-radius: 6px;

  .v-icon { color: inherit; }
  &:hover { color: var(--cc-text, #1f242d); background: var(--cc-canvas, #f6f7f9); }
}

.lvl-anchw-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.03em;
  color: var(--cc-accent, #1d5bd6);
  margin-bottom: 4px;

  .v-icon { color: inherit; }
}

.lvl-anchw-rail {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 20px 12px;
  border-bottom: 1px solid var(--cc-border-soft, #edf0f7);
}
.lvl-anchw-railstep {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 10px 5px 6px;
  border: none;
  background: transparent;
  border-radius: 999px;
  font: inherit;
  font-size: 12px;
  font-weight: 650;
  color: var(--cc-text-3, #97a0af);
  cursor: default;

  &:not(:disabled) { cursor: pointer; }

  .n {
    flex: none;
    width: 20px;
    height: 20px;
    border-radius: 999px;
    display: grid;
    place-items: center;
    font-size: 11px;
    background: var(--cc-canvas, #f6f7f9);
    border: 1px solid var(--cc-border, #e5e7eb);

    .v-icon { color: inherit; }
  }

  /* Never break a step's own label: wrapping mid-label reads as two steps. The rail
     itself does not wrap either (the prototype's does not), so all six mapping steps
     stay on one line — which they fit on once the connectors are out of the flow. */
  .l { white-space: nowrap; }

  &.is-done {
    color: var(--cc-text-2, #525c6b);

    .n {
      background: color-mix(in srgb, var(--cc-accent, #1d5bd6) 12%, transparent);
      border-color: transparent;
      color: var(--cc-accent, #1d5bd6);
    }
  }

  &.is-now {
    color: var(--cc-text, #1f242d);
    background: color-mix(in srgb, var(--cc-accent, #1d5bd6) 8%, transparent);

    .n {
      background: var(--cc-accent, #1d5bd6);
      border-color: var(--cc-accent, #1d5bd6);
      color: #fff;
    }
  }

  /* The connector lives in the step's MARGIN, absolutely positioned — never in flow.
     In flow it sat inside the step's padding box, so the .is-now pill painted its
     background behind the connector (the highlight visibly ran through the dash) and
     every step carried an extra 16px of width, which pushed the six-step mapping rail
     onto a second line. Positioned in the margin it affects neither. */
  + .lvl-anchw-railstep {
    position: relative;
    margin-left: 16px;

    &::before {
      content: '';
      position: absolute;
      left: -14px;
      top: 50%;
      width: 12px;
      height: 1px;
      background: var(--cc-border, #e5e7eb);
    }
  }
}

.lvl-modal-body { padding: 16px 20px; }
.lvl-anchw-body { max-height: min(56vh, 540px); overflow-y: auto; }
.lvl-anchw-p {
  margin: 0 0 12px;
  font-size: 13.5px;
  color: var(--cc-text-2, #525c6b);
  line-height: 1.55;

  &--dim { font-size: 12px; color: var(--cc-text-3, #97a0af); }
}
.lvl-anchw-steps {
  margin: 0 0 14px;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 13px;
  color: var(--cc-text-2, #525c6b);
  line-height: 1.5;
}
.lvl-anchw-note-info {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 10px 12px;
  margin-bottom: 12px;
  border-radius: 10px;
  background: color-mix(in srgb, var(--cc-accent, #1d5bd6) 7%, transparent);
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--cc-text-2, #525c6b);

  .v-icon { flex: none; color: var(--cc-accent, #1d5bd6); }

  &.is-warn {
    background: color-mix(in srgb, var(--cc-warn-fg, #8a6a00) 10%, transparent);

    .v-icon { color: var(--cc-warn-fg, #8a6a00); }
  }
}

.lvl-anchw-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 9px;
  background: var(--cc-surface, #fff);
  margin-bottom: 10px;
  color: var(--cc-text-3, #97a0af);

  .v-icon { color: inherit; }

  input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font: inherit;
    font-size: 13px;
    color: var(--cc-text, #1f242d);
  }

  &--sm { flex: 1 1 200px; margin-bottom: 0; padding: 6px 10px; }
}

.lvl-anchw-list { display: flex; flex-direction: column; gap: 6px; }
.lvl-anchw-opt {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 10px;
  background: var(--cc-surface, #fff);
  cursor: pointer;
  color: var(--cc-accent, #1d5bd6);

  .v-icon { color: inherit; }
  &:hover { border-color: color-mix(in srgb, var(--cc-accent, #1d5bd6) 45%, transparent); }

  &.is-on {
    border-color: var(--cc-accent, #1d5bd6);
    box-shadow: 0 0 0 1px var(--cc-accent, #1d5bd6) inset;
    background: color-mix(in srgb, var(--cc-accent, #1d5bd6) 5%, var(--cc-surface, #fff));
  }

  &--check input { margin: 0 2px 0 0; }
}
.lvl-anchw-opt-main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;

  .t { font-size: 13.5px; font-weight: 650; color: var(--cc-text, #1f242d); }
  .s { font-size: 12px; color: var(--cc-text-3, #97a0af); }
}
.lvl-anchw-needs { flex: none; font-size: 11px; font-weight: 650; color: var(--cc-warn-fg, #8a6a00); }
.lvl-anchw-code {
  flex: none;
  font-size: 11.5px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
  border: 1px solid var(--cc-border, #e5e7eb);
  background: var(--cc-canvas, #f6f7f9);
  color: var(--cc-text-2, #525c6b);
}
.lvl-anchw-empty { padding: 14px; font-size: 12.5px; color: var(--cc-text-3, #97a0af); text-align: center; }
.lvl-anchw-subhead {
  margin: 16px 0 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--cc-text, #1f242d);

  .s { font-weight: 500; color: var(--cc-text-3, #97a0af); margin-left: 6px; }
}

/* Indicative incumbent count. Dashed + warn when it's a one-off, because that is
   the case the scatter step is warning about. */
.lvl-anchw-hc {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex: none;
  font-size: 10.5px;
  font-weight: 700;
  color: var(--cc-text-3, #97a0af);
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 999px;
  padding: 2px 7px;
  cursor: help;

  .v-icon { color: inherit; }

  &.is-one {
    border-style: dashed;
    color: var(--cc-warn-fg, #8a6a00);
    border-color: color-mix(in srgb, var(--cc-warn-fg, #8a6a00) 50%, transparent);
  }
}

.lvl-anchw-sugg-tag {
  font-size: 10.5px;
  font-weight: 700;
  color: var(--cc-accent, #1d5bd6);
  border: 1px solid color-mix(in srgb, var(--cc-accent, #1d5bd6) 40%, transparent);
  border-radius: 999px;
  padding: 2px 8px;
  margin-left: 2px;
}
.lvl-anchw-nosugg {
  font-size: 10.5px;
  font-weight: 650;
  color: var(--cc-text-3, #97a0af);
  border: 1px dashed var(--cc-border, #e5e7eb);
  border-radius: 999px;
  padding: 2px 7px;
  margin-left: 2px;
  cursor: help;
}

.lvl-anchw-fams { display: flex; flex-direction: column; gap: 8px; }
.lvl-anchw-fam {
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 10px;
  padding: 10px 12px;
  background: var(--cc-surface, #fff);
  display: flex;
  flex-direction: column;
  gap: 8px;

  &.is-off { opacity: 0.62; }
}
.lvl-anchw-fam-check {
  display: flex;
  align-items: center;
  gap: 9px;
  cursor: pointer;
  flex-wrap: wrap;
  margin: 0;

  &.is-disabled { opacity: 0.5; cursor: not-allowed; }

  .fam { font-size: 13px; font-weight: 700; color: var(--cc-text, #1f242d); }
  .n { font-size: 11.5px; color: var(--cc-text-3, #97a0af); }
}
.lvl-anchw-fam-pick { display: flex; align-items: center; gap: 10px; }
.lvl-anchw-selwrap { flex: 1 1 auto; min-width: 0; }
/* Native select with our own chevron, as the prototype does — one role per level /
   per family, so a multi-select would misrepresent the choice. */
.lvl-selwrap {
  position: relative;
  display: inline-flex;
  align-items: center;

  select {
    width: 100%;
    appearance: none;
    padding: 6px 30px 6px 10px;
    border: 1px solid var(--cc-border, #e5e7eb);
    border-radius: 8px;
    background: var(--cc-surface, #fff);
    color: var(--cc-text, #1f242d);
    font: inherit;
    font-size: 12.5px;
    cursor: pointer;

    &:focus { outline: none; border-color: var(--cc-accent, #1d5bd6); }
  }

  > .v-icon {
    position: absolute;
    right: 7px;
    pointer-events: none;
    color: var(--cc-text-3, #97a0af);
  }
}
.lvl-anchw-skip { font-size: 11.5px; color: var(--cc-text-3, #97a0af); }

.lvl-anchw-spine { display: flex; flex-direction: column; gap: 6px; margin-top: 2px; }
.lvl-anchw-spinerow {
  /* GRID, not flex: the header row above uses the same template, so every cell
     lines up under its label. Flex left the columns ragged. */
  display: grid;
  grid-template-columns: auto 62px minmax(0, 1fr) 46px 140px;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  border: 1px solid var(--cc-border-soft, #edf0f7);
  border-radius: 9px;
  background: var(--cc-canvas, #f6f7f9);

  &.is-off { opacity: 0.6; }
}

.lvl-anchw-state {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px solid var(--cc-border, #e5e7eb);
  color: var(--cc-text-3, #97a0af);

  .v-icon { color: inherit; }

  &.is-ok {
    color: var(--cc-ok-fg, #1f7a53);
    border-color: color-mix(in srgb, var(--cc-ok-fg, #1f7a53) 40%, transparent);
  }
  &.is-on {
    color: var(--cc-accent, #1d5bd6);
    border-color: color-mix(in srgb, var(--cc-accent, #1d5bd6) 40%, transparent);
  }
  &.is-pend {
    color: var(--cc-warn-fg, #8a6a00);
    border-color: color-mix(in srgb, var(--cc-warn-fg, #8a6a00) 45%, transparent);
    border-style: dashed;
  }
}

.lvl-anchw-fambar { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
.lvl-anchw-fambar-r { margin-left: auto; display: flex; align-items: center; gap: 6px; }
.lvl-anchw-bulk {
  border: 1px solid var(--cc-border, #e5e7eb);
  background: var(--cc-surface, #fff);
  border-radius: 8px;
  padding: 4px 10px;
  font: inherit;
  font-size: 11.5px;
  font-weight: 650;
  color: var(--cc-text-2, #525c6b);
  cursor: pointer;

  &:hover { border-color: var(--cc-text-3, #97a0af); color: var(--cc-text, #1f242d); }
}
.lvl-anchw-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 999px;
  background: var(--cc-surface, #fff);
  font: inherit;
  font-size: 11.5px;
  font-weight: 650;
  color: var(--cc-text-2, #525c6b);
  cursor: pointer;

  .v-icon { color: inherit; }

  &.is-on {
    border-color: var(--cc-accent, #1d5bd6);
    color: var(--cc-accent, #1d5bd6);
    background: color-mix(in srgb, var(--cc-accent, #1d5bd6) 8%, transparent);
  }
}

.lvl-anchw-grp { margin-bottom: 14px; }
.lvl-anchw-grp-h {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.03em;
  color: var(--cc-text-3, #97a0af);
  padding: 0 2px 7px;

  .n { font-weight: 500; }
}

.lvl-anchw-covstats { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
.lvl-anchw-covstat {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  padding: 8px 12px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 10px;
  background: var(--cc-surface, #fff);
  font-size: 12px;
  color: var(--cc-text-2, #525c6b);

  b { font-size: 17px; font-weight: 750; color: var(--cc-text, #1f242d); }

  &.is-proj {
    border-color: color-mix(in srgb, var(--cc-accent, #1d5bd6) 40%, transparent);

    b { color: var(--cc-accent, #1d5bd6); }
  }
  &.is-gap b { color: var(--cc-warn-fg, #8a6a00); }
}

.lvl-anchw-gaps { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 12px; }
.lvl-anchw-gapchip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 9px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 999px;
  font-size: 12px;
  font-weight: 650;
  color: var(--cc-text, #1f242d);
  cursor: help;
}
.lvl-anchw-gapmore { align-self: center; font-size: 11.5px; color: var(--cc-text-3, #97a0af); }
/* Nothing at all vs thin: a level with zero evidence reads differently from one
   that is merely short of the floor. */
.lvl-fw-cov {
  font-weight: 700;

  &.is-gap { color: var(--cc-warn-fg, #8a6a00); }
  &.is-thin { color: var(--cc-text-3, #97a0af); }
}

.lvl-anchw-rev {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 11px;
  overflow: hidden;
}
.lvl-anchw-rev-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 13px 15px;
  background: var(--cc-surface, #fff);
  border-bottom: 1px solid var(--cc-border-soft, #edf0f7);

  &:last-child { border-bottom: none; }
}
.lvl-anchw-rev-ic {
  flex: none;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  display: grid;
  place-items: center;
  background: color-mix(in srgb, var(--cc-accent, #1d5bd6) 10%, transparent);
  color: var(--cc-accent, #1d5bd6);

  .v-icon { color: inherit; }

  &.is-warn {
    background: color-mix(in srgb, var(--cc-warn-fg, #8a6a00) 12%, transparent);
    color: var(--cc-warn-fg, #8a6a00);
  }
}
.lvl-anchw-rev-main {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  .h { font-size: 13px; font-weight: 700; color: var(--cc-text, #1f242d); }
  .b { font-size: 12.5px; color: var(--cc-text-2, #525c6b); line-height: 1.5; }
}

/* The review step's closing note, in place of the removed rationale box. Reuses
   .lvl-note--info's shell; only the top margin is local. */
.lvl-anchw-revnote { margin-top: 14px; }

.lvl-anchw-done { display: flex; align-items: flex-start; gap: 12px; }
.lvl-anchw-done-ic {
  flex: none;
  width: 40px;
  height: 40px;
  border-radius: 11px;
  display: grid;
  place-items: center;
  background: color-mix(in srgb, var(--cc-accent, #1d5bd6) 12%, transparent);
  color: var(--cc-accent, #1d5bd6);

  .v-icon { color: inherit; }
}
.lvl-anchw-tasks {
  margin-top: 6px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 11px;
  overflow: hidden;
}
.lvl-anchw-tasks-h,
.lvl-anchw-tasks-f {
  padding: 9px 13px;
  font-size: 11.5px;
  color: var(--cc-text-3, #97a0af);
  background: var(--cc-canvas, #f6f7f9);
}
.lvl-anchw-tasks-h { font-weight: 700; letter-spacing: 0.03em; }
.lvl-anchw-tasks-f { line-height: 1.5; border-top: 1px solid var(--cc-border-soft, #edf0f7); }
.lvl-anchw-task {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 13px;
  font-size: 13px;
  color: var(--cc-text, #1f242d);
  border-top: 1px solid var(--cc-border-soft, #edf0f7);

  .v-icon { color: var(--cc-text-3, #97a0af); }
  .fam { font-size: 11.5px; color: var(--cc-text-3, #97a0af); margin-right: auto; }
}

.lvl-modal-foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid var(--cc-border-soft, #edf0f7);
}
.lvl-anchw-foot { flex-wrap: wrap; }
.lvl-anchw-count { margin-right: auto; font-size: 12px; color: var(--cc-text-3, #97a0af); }
.lvl-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 16px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 9px;
  background: var(--cc-surface, #fff);
  color: var(--cc-text, #1f242d);
  font: inherit;
  font-size: 13.5px;
  font-weight: 650;
  cursor: pointer;

  .v-icon { color: inherit; }

  &--text { border-color: transparent; background: transparent; color: var(--cc-text-2, #525c6b); }
  &--primary {
    background: var(--cc-accent, #1d5bd6);
    border-color: var(--cc-accent, #1d5bd6);
    color: #fff;
  }
  &:disabled { opacity: 0.5; cursor: default; }
}

/* Bulk-save spinner. The batch is N sequential requests, so the button carries a
   determinate count beside it rather than an indefinite "please wait". */
.lvl-anchw-spin {
  flex: none;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid color-mix(in srgb, currentColor 30%, transparent);
  border-top-color: currentColor;
  animation: lvl-anchw-spin 0.7s linear infinite;
}
@keyframes lvl-anchw-spin { to { transform: rotate(360deg); } }
/* Respect a reduced-motion preference: keep the affordance, drop the rotation. */
@media (prefers-reduced-motion: reduce) {
  .lvl-anchw-spin { animation-duration: 2.4s; }
}


/* Column header for the spine rows — same grid template, so labels sit over values. */
.lvl-anchw-spinehead {
  display: grid;
  grid-template-columns: auto 62px minmax(0, 1fr) 46px 140px;
  align-items: center;
  gap: 10px;
  padding: 0 8px;
  border: 1px solid transparent;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--cc-text-2, #525c6b);
}
.lvl-anchw-spinehead > span:first-child { width: 20px; }
/* Chips fill their grid cell by default; keep them their natural width. */
.lvl-anchw-spinerow > .lvl-anchw-hc,
.lvl-anchw-spinerow > .lvl-anchw-state { justify-self: start; }
/* A skipped level has no role/people/status, so its note spans those columns. */
.lvl-anchw-spinerow .lvl-anchw-skip { grid-column: 3 / -1; }

/* Legend above the gap chips. */
.lvl-anchw-gapshead {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 10px;
  margin: 14px 0 8px;
  font-size: 12px;
  font-weight: 700;
  color: var(--cc-text, #1f242d);
}
.lvl-anchw-gapshead .s {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-weight: 500;
  font-size: 11.5px;
  color: var(--cc-text-3, #97a0af);
}

/* A weak/indicative badge reads as a caution, not a recommendation — the difference
   between "we found this" and "we guessed this, check it". */
.lvl-anchw-sugg-tag.is-weak {
  color: var(--cc-warn-fg, #8a6a00);
  border-color: color-mix(in srgb, var(--cc-warn-fg, #8a6a00) 40%, transparent);
  background: color-mix(in srgb, var(--cc-warn-fg, #8a6a00) 8%, transparent);
}

/* A job-title tier standing in for a level. Deliberately NOT styled as a level chip:
   it is a display grouping and must not be mistaken for a grade. */
.lvl-anchw-tier {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border: 1px dashed var(--cc-border, #e5e7eb);
  border-radius: 6px;
  background: transparent;
  color: var(--cc-text-2, #525c6b);
  font-size: 11px;
  font-weight: 650;
  white-space: nowrap;
  cursor: help;
}

/* A claimed option stays visible but unpickable, so the reason is legible. */
.lvl-anchw-selwrap select option:disabled { color: var(--cc-text-3, #97a0af); }

/* ---- "What counts as a role?" doc-type scope ---------------------------------
   Ported rule-for-rule from the prototype's .lvl-anchw-doctype* block in
   lvl/lvl.css, with --v3-* mapped onto the --cc-* tokens theme.scss declares
   globally for light and dark (the same mapping every other rule in this file
   uses, so dark mode recomputes the color-mix washes for free). */
.lvl-anchw-doctypes {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 14px 0 2px;
  padding: 12px 14px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 10px;
  background: var(--cc-canvas, #f6f7f9);
}
.lvl-anchw-doctypes .h {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 700;
  color: var(--cc-text, #1f242d);
}
.lvl-anchw-doctypes .s {
  font-size: 11.5px;
  color: var(--cc-text-2, #525c6b);
  line-height: 1.45;
}
.lvl-anchw-doctypes .s.is-warn { color: var(--cc-warn-fg, #8a6a00); font-weight: 650; }
.lvl-anchw-doctype-row { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 6px; }
.lvl-anchw-doctype {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 10px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 8px;
  background: var(--cc-surface, #fff);
  font-size: 12.5px;
  cursor: pointer;
}
.lvl-anchw-doctype.is-on {
  border-color: color-mix(in srgb, var(--cc-accent, #1d5bd6) 45%, transparent);
  background: color-mix(in srgb, var(--cc-accent, #1d5bd6) 6%, var(--cc-surface, #fff));
}
.lvl-anchw-doctype .t { font-weight: 650; color: var(--cc-text, #1f242d); }
.lvl-anchw-doctype .n {
  font-size: 11px;
  color: var(--cc-text-2, #525c6b);
  padding: 1px 6px;
  border-radius: 999px;
  background: var(--cc-canvas, #f6f7f9);
}

/* The "grouped by job title" explainer at the top of a level-less spine. */
.lvl-anchw-spinenote { margin: 2px 0 8px; }
</style>
